import {FC, useEffect, useMemo, useState} from "react";
import Breadcrumb from "../../components/common/Breadcrumbs";
import {hasPermission, jwtDecode} from "../../helpers/utils/AccessControlUtils";
import {
    useFindUserSettingsByGroupNameQuery
} from "../../components/common/userSetting/userSettingsApi";
import {
    createClickableLink,
    SearchSelectWithIndeterminateBoolean
} from "../../components/common/datatable/DataTableUtils";
import {formatValueAsDate, formatValueAsDateAndTime} from "../../helpers/utils/DateUtils";
import {useLazyExportProductDataQuery} from "./productsApi";
import {DataTable} from "../../components/common/datatable/DataTable";
import {SearchInputWrapper} from "../../components/common/datatable/filter/SearchInputWrapper";
import {SearchSelectWithEnum, SearchSelectWithList} from "../../components/common/datatable/filter/SearchSelectWrapper";
import {useGetEnumsQuery} from "../../app/globalApi";
import {useLocation, useNavigate} from "react-router-dom";
import {SearchDatePickerWrapper} from "../../components/common/datatable/filter/SearchDatePickerWrapper";
import {useGetAllCustomersListQuery} from "../customer/customerApi";
import {FaPlus} from "react-icons/fa6";
import IconButton from "../../components/common/form/IconButton";
import {getEnumDisplayNameByConstant} from "../../helpers/utils/EnumUtils";
import {useSearchFollowUpTypesQuery} from "../tenantAdmin/followUpTypes/followUpTypesApi";
import ColorTag from "./ColorTag";
import {DataTableColumn} from "../../components/common/datatable/DataTableTypes";
import {
    appendNewOption,
    getProductStatusDotInfo,
    getShortProductName,
    getStateValueFromParam
} from "../../helpers/utils/Utils";
import {truncateText} from "../../helpers/utils/TextUtils";
import {useSearchOrdersQuery} from "./ordersApi";
import {useGetAllStatesQuery} from "../data/states/statesApi";
import {useLazyGetAllCountyByStateAbbrQuery} from "../data/counties/countiesApi";
import {useGetAllUsersListQuery} from "../user/users/usersApi";
import {useLazyGetBranchByCustomerIdQuery} from "../customer/branchContactApi";
import AddFollowUpForProduct from "./modal/AddFollowUpForProduct";

const Orders: FC = () => {

    const navigate = useNavigate()

    const [crumbs] = useState([
        {content: "Home", url: "/home"},
        {content: "Orders", url: ""},
    ])
    const {
        data: customerData,
    } = useGetAllCustomersListQuery("", {
        skip: !hasPermission(["CUST-R"]),
    });
    const [branchApi, branchData] = useLazyGetBranchByCustomerIdQuery();
    const state = getStateValueFromParam(window);
    const followUpTypes = useSearchFollowUpTypesQuery({pageSize: 100}, {
        skip: !hasPermission(["FOLLUP-R"]),
    });
    const [customFollowUpList, setCustomFollowUpList] = useState<any>([]);
    const {data: userSettings} = useFindUserSettingsByGroupNameQuery("Orders");
    const {data: enums} = useGetEnumsQuery("");
    const [triggerExportProductDataQuery] = useLazyExportProductDataQuery();
    const [showFollowUpModal, setShowFollowUpModal] = useState(false);
    const [curProduct, setCurProduct] = useState({});
    const [orderId, setOrderId] = useState("");
    const stateData = useGetAllStatesQuery("", {
        skip: !hasPermission(["STAT-R"]),
    });
    const userList = useGetAllUsersListQuery("", {
        skip: !hasPermission(["USR-R"]),
    });

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const defaultStatuses = [
        "NEW", "COMPLETED", "PENDING", "AWAITING_LOCATE",
        "ISSUE", "AWAITING_PARTNER", "AWAITING_FULFILLMENT"
    ];
    let queryObject: Record<string, any> = {};
    if (![...searchParams.keys()].length) {
        queryObject = {
            state: "TX",
            orderStatus: defaultStatuses

        };
    } else {
        queryObject = Object.fromEntries(searchParams.entries());

        // ✅ Convert any array-style keys like orderStatus[]
        for (const [key] of searchParams.entries()) {
            if (key.endsWith("[]")) {
                const baseKey = key.replace("[]", "");
                queryObject[baseKey] = searchParams.getAll(key);
                delete queryObject[key];
            }
        }
    }
    const [defaultFilter] = useState(queryObject);

    const [countyDataApi, countyApi] = useLazyGetAllCountyByStateAbbrQuery();
    useEffect(() => {
        document.title = "Orders";
    });


    useEffect(() => {
        countyDataApi(state);
    }, [state]);


    useEffect(() => {
        if (followUpTypes?.data) {
            const updatedFollowUpList = [
                {id: 'no-follow-up', name: 'No Follow-up', hexColor: '#FFFFFF'}, // Custom option
                {id: 'any', name: 'Any', hexColor: '#808080'}, // Custom option
                ...followUpTypes?.data.data
            ];
            setCustomFollowUpList(updatedFollowUpList);
        }
    }, [followUpTypes?.data]);

    let quickFilters = useMemo(() => [
        {
            Label: "Active Orders",
            keyVal: {
                orderStatus: ["NEW", "COMPLETED", "PENDING", "AWAITING_LOCATE", "ISSUE", "AWAITING_PARTNER", "AWAITING_FULFILLMENT"],
                customerRetrieved: "",
                quickFilters: "active_order_quick_filter",
                customStyle: "bg-highlight-7"
            },
            Cell: (
                <i className="fas fa-cog" style={{color: "white"}} title="Active Orders"/>
            ),
            defaultGridQuery: {
                state: "TX",
                orderStatus: ["NEW", "COMPLETED", "PENDING", "AWAITING_LOCATE", "ISSUE", "AWAITING_PARTNER", "AWAITING_FULFILLMENT"],
                type: [""],
                productStatuses: [""],
                customerRetrieved: ""
            },
            className: "active_order_quick_filter"

        },
        {
            Label: "Pending Orders",
            keyVal: {
                "orderStatus": ["PENDING", "AWAITING_PARTNER", "AWAITING_LOCATE", "AWAITING_FULFILLMENT"],
                "customerRetrieved": "",
                "quickFilters": "pending_order_quick_filter",
                customStyle: "bg-warn-1"
            },
            Cell: (
                <i className="fas fa-hourglass" style={{color: "white"}} title={"Pending Orders"}/>
            ),
            className: "pending_order_quick_filter"

        },
        {
            Label: "Pending Orders Tax",
            keyVal: {
                "productStatuses": ["PENDING", "SAVED"],
                "orderStatus": [""],
                "type": ["TAX_CERTIFICATE", "TAX_REPORT"],
                "customerRetrieved": "",
                "quickFilters": "pending_order_tax_quick_filter",
                customStyle: "bg-highlight-5"
            },
            Cell: (
                <i className="fas fa-calculator" style={{color: "white"}} title={"Pending Orders Tax"}/>
            ),
            className: "pending_order_tax_quick_filter"

        },
        {
            Label: "Pending Orders HOA",
            keyVal: {
                "productStatuses": ["PENDING", "SAVED"],
                "orderStatus": [""],
                "type": ["HOA_FULL", "HOA_CONTACT"],
                "customerRetrieved": "",
                "quickFilters": "pending_order_hoa_quick_filter",
                "customStyle": "bg-highlight-1"
            },
            Cell: (
                <i className="fas fa-calculator" style={{color: "white"}} title={"Pending Orders HOA"}/>
            ),
            className: "pending_order_hoa_quick_filter"
        },
        {
            Label: "Non Retrieved Delivered Orders",
            keyVal: {
                "orderStatus": ["DELIVERED"],
                "customerRetrieved": false,
                "quickFilters": "non_retrieved_delivered_order_quick_filter",
                "customStyle": "bg-surface-9"
            },
            Cell: (
                <i className="fa fa-server" style={{color: "white"}} title={"Non Retrieved Delivered Orders"}/>
            ),
            className: "non_retrieved_delivered_order_quick_filter"
        }
    ], []);

    const columns: DataTableColumn[] = [
        {
            label: <i title="Reorder" className="fas fa-history fa-sm"/>,
            accessor: "reorderNumber",
            cell: (props: any) => <>{props.value}</>,
        },
        {
            label: "Order No.",
            accessor: "customerOrderNumber",
            cell: (props: any) => {
                let path = `/orders/${props.row.original.id}`;
                return createClickableLink(path, props.value, userSettings, false, "", ["ORDR-R", "ORDR-V"]);
            },
            filterOptions: [{
                filterComponent: <SearchInputWrapper
                    accessor={'customerOrderNumber'}
                    placeholder={'Enter Order Number...'}
                    name={'orderNumber'}/>
            }]
        },
        {
            label: "Status",
            accessor: "orderStatus",
            cell: (props: any) => {
                return <>
                    <span>{getEnumDisplayNameByConstant(enums?.["OrderStatus"], props.value)}</span>
                </>
            },
            filterOptions: [{
                filterComponent: SearchSelectWithEnum(enums?.["OrderStatus"], "Select Order Status(es)...", true, "orderStatus", null),
                overrideFilterAccessor: "orderStatus",
                overrideFilterLabel: "Order Status"
            }]
        },

        {
            label: "Order Date", accessor: "creationDate", cell: (props: any) => {
                return formatValueAsDateAndTime(props.row.original.creationDate);
            },
            filterOptions: [{
                filterComponent: <SearchDatePickerWrapper accessor={"createdDateFrom"}/>,
                overrideFilterAccessor: "createdDateFrom",
                overrideFilterLabel: "Created Date - From",
            }, {
                filterComponent: <SearchDatePickerWrapper accessor={"createdDateTo"}/>,
                overrideFilterAccessor: "createdDateTo",
                overrideFilterLabel: "Created Date - To",
            }]
        },

        {
            label: "Delivered Date", accessor: "lastDeliveredDate",
            visibility: {
                canColumnShow: false,
                showByDefault: false,
                hasShowSetting: false,
            },
            filterOptions: [{
                filterComponent: <SearchDatePickerWrapper accessor={"deliveredDateFrom"}/>,
                overrideFilterAccessor: "deliveredDateFrom",
                overrideFilterLabel: "Delivered Date - From",
            }, {
                filterComponent: <SearchDatePickerWrapper accessor={"deliveredDateTo"}/>,
                overrideFilterAccessor: "deliveredDateTo",
                overrideFilterLabel: "Delivered Date - To",
            }]
        },
        {
            label: "Products",
            accessor: "type",
            canSort: false,
            cell: (props: any) => {
                const products = [...props.row.original.products];

                return (
                    <>
                        {products
                            .sort((a: { type: string }, b: { type: string }) =>
                                a.type.localeCompare(b.type)
                            )
                            .map((product: any) => {
                                const dotInfo = getProductStatusDotInfo(product.productStatus);

                                return (
                                    <div className="product-div" key={product.id}>
                                        {hasPermission(["PROFOLLUP-V"]) && product.followUp !== null && (
                                            <i
                                                className={`fa fa-solid fa-tag mr-1 text-xs product_status_circle tag_icon ${hasPermission(["PROFOLLUP-R"]) ? "cursor-pointer " : ""
                                                }`}
                                                onClick={(e) => {
                                                    if (hasPermission(["PROFOLLUP-R"])) {
                                                        e.preventDefault();
                                                        setCurProduct(product);
                                                        setOrderId(props.row.original.id);
                                                        setShowFollowUpModal(true);
                                                    }
                                                }}
                                                title={
                                                    product.followUpTypeDTO
                                                        ? `${product.followUpTypeDTO.name} : ${formatValueAsDate(
                                                            product.followUp.followUpAdded,
                                                            "MM/DD/YY"
                                                        )} ${product.followUp.followUpMessage}`
                                                        : ""
                                                }
                                                style={{
                                                    color: product.followUpTypeDTO?.hexColor || "",
                                                }}
                                            />
                                        )}

                                        {jwtDecode()?.customerLevel &&
                                            jwtDecode()?.customerLevel !== "CUSTOMER" &&
                                            product.assignedTo !== null && (
                                                <i
                                                    title={`${product.assignedTo} (${dotInfo.title})`}
                                                    className="fa fa-user mr-1 text-xs default-cursor"
                                                    style={{color: dotInfo.color}}
                                                />
                                            )}
                                        {jwtDecode()?.customerLevel &&
                                            jwtDecode()?.customerLevel !== "CUSTOMER" &&
                                            product.assignedTo === null && (
                                                <i
                                                    className="product_status_circle  text-xs fa fa-solid fa-circle mr-1 default-cursor"
                                                    style={{color: dotInfo.color}}
                                                    title={dotInfo.title}
                                                />
                                            )}

                                        <span>{getShortProductName(product.type)}</span>
                                    </div>
                                );
                            })}
                    </>
                );
            },
            filterOptions: [
                {
                    filterComponent: SearchSelectWithEnum(
                        enums?.["ProductType"] &&
                        enums?.["ProductType"].filter(
                            (p: { value: string | string[] }) => !p.value.includes("UNKNOWN")
                        ),
                        "Select Products...",
                        true,
                        "type"
                    ),
                    overrideFilterLabel: "Products",
                },
            ],
        },
        {
            label: "Product Statuses",
            accessor: "productStatuses",
            visibility: {canColumnShow: false},
            cell: (props: any) => {
                return <>
                    <span>
                        {getEnumDisplayNameByConstant(enums?.["ProductStatus"], props.value)}
                    </span>
                </>
            },
            filterOptions: [{
                filterComponent: SearchSelectWithEnum(enums?.["ProductStatus"], "Select Product Statuses...", true, "productStatuses"),
            }],
        },
        {
            label: "Assigned To",
            accessor: "assignedTo",
            visibility: {canColumnShow: false},
            filterOptions: [{
                filterComponent: SearchSelectWithList(userList.data && Array.isArray(userList.data) && appendNewOption(userList?.data, {
                    id: "Unassigned",
                    username: "Unassigned"
                }), "username", "id", "Select Assigned To...", true, "assignedTo"),
                overrideFilterAccessor: "assignedTo",
            }],
        },
        {
            label: "State", accessor: "state",
            visibility: {canColumnShow: false},
            filterOptions: [{
                dependentColumns: ["County"],
                filterComponent: stateData?.data ? SearchSelectWithList(stateData?.data, (state: any) => `${state.stateName} `, "stateAbbreviation", "Select State...", false, "state") : [],
                overrideFilterAccessor: "state"
            }],
        },
        {
            label: "County",
            accessor: "county",
            filterOptions: [{
                filterComponent: SearchSelectWithList(countyApi?.data, (county: any) => `${county.countyName}`, "id", "Select County...", false, "county"),
                onDependencyUpdated: (value: string) => {
                    countyDataApi(value);
                }
            }],
            canSort: false,
        },
        {
            label: "Address", accessor: "addressLine1",
            cell: (props: any) => truncateText(props.value, 15),
            filterOptions: [{
                filterComponent: <SearchInputWrapper
                    accessor={'addressLine1'}
                    placeholder={'Enter Address...'}
                    name={'addressLine1'}/>
            }]
        },
        {
            label: "Legal Description", accessor: "legalDescription",

            cell: (props: any) => truncateText(props.value, 18),
            filterOptions: [{
                filterComponent: <SearchInputWrapper
                    accessor={'addressLine1'}
                    placeholder={'Enter Address...'}
                    name={'addressLine1'}/>
            }]
        },

        {
            label: "Partner", accessor: "originalOrderSource",
            cell: (props: any) => {
                return <>
                    <span>{getEnumDisplayNameByConstant(enums?.["OrderSource"], props.value)}</span>
                </>
            }, filterOptions: [{
                filterComponent: SearchSelectWithEnum(enums?.["OrderSource"] && enums?.["OrderSource"].filter((p: any) => !p.value.includes("UNKNOWN")), "Select Partners...", true, "partners"),
                overrideFilterAccessor: "partners",
            }],
            visibility: {showByDefault: false}
        },
        {
            label: "Customer",
            accessor: "customerDTO.customerName",
            overrideSortColumn: "customerId",
            filterOptions: [{
                dependentColumns: ["Branch"],
                filterComponent: SearchSelectWithList(customerData, (customer: any) => `${customer.customerName} (${customer.customerNumber})`, "id", "Select Customer...", false, "customerId"),
                overrideFilterAccessor: "customerId",
            }],
        },
        {
            label: "Branch", accessor: "branchDTO.name",
            overrideSortColumn: "branchId",
            filterOptions: [{
                filterComponent: SearchSelectWithList(branchData?.data?.data, "name", "id", "Select Branches...", true, "branchIds"),
                onDependencyUpdated: (value: any) => {
                    branchApi(value);
                },
                overrideFilterAccessor: "branchIds",
            }]
        },
        {
            label: "Buyer", accessor: "buyer", visibility: {canColumnShow: false}
            , filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Enter Buyer..."} name={"buyer"}
                                                     accessor={"buyer"}/>,
            }]
        }, {
            label: "Seller", accessor: "seller", visibility: {canColumnShow: false},
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Enter Seller..."} name={"seller"}
                                                     accessor={"seller"}/>,
            }]
        },
        {
            label: <i title="Rush" className="fa fa-clock fa-sm"/>,
            accessor: "rushDate",
            cell: (props: any) => <>
                {props.value ? <i title="Rush" className="fa fa-clock fa-sm default-cursor"/> : ''}
            </>,
            filterOptions: [{
                filterComponent: SearchSelectWithIndeterminateBoolean("Select Rush...", "rush"),
                overrideFilterLabel: "Rush",
                overrideFilterAccessor: "rush"
            }]
        },
        {
            label: <i title="Has Error" className="fas fa-exclamation-circle fa-sm"/>,
            accessor: "lastErrorDate",
            cell: (props: any) => <>{props.value ? <i
                    title={formatValueAsDate(props.value) ?? undefined}
                    className="fa fa-exclamation-circle fa-sm dateConversion-last-error default-cursor"
                    style={{color: "#d9342f"}}
                />
                : ''}
            </>,
            filterOptions: [{
                filterComponent: SearchSelectWithIndeterminateBoolean("Select Has Error...", "hasError"),
                overrideFilterAccessor: "hasError",
                overrideFilterLabel: "Has Error",
            }]
        },
        {
            label: <i title="Has Note" className="fa fa-sticky-note fa-sm default-cursor"/>,
            accessor: "hasNotes",

            // visibility: {canColumnShow: false},
            cell: (props: any) => <>
                {props.value === true ? <i title={"Has Note"} className="fa fa-sticky-note fa-sm default-cursor"/> : ''}
            </>,
            filterOptions: [{
                filterComponent: SearchSelectWithIndeterminateBoolean("Select Has Note...", "hasNotes"),
                overrideFilterAccessor: "hasNotes",
                overrideFilterLabel: "Has Note",
            }]
        },
        {
            label: "Customer Retrieved",
            accessor: "customerRetrieved",
            cell: (props: any) => {
                if (props.value === true) {
                    return <i className="fa fa-check-circle enabled-data align--center enabled default-cursor"></i>;
                } else if (props.value === false) {
                    return <i className="fa fa-times-circle disabled-data align--center enabled default-cursor"
                              aria-hidden="true"></i>;
                } else {
                    return null;
                }
            },
            filterOptions: [{
                filterComponent: SearchSelectWithIndeterminateBoolean("Select Customer Retrieved...", "customerRetrieved", "True", "False"),
                overrideFilterAccessor: "customerRetrieved",
                overrideFilterLabel: "Customer Retrieved",
            }]
        },
        {
            label: "Follow Up Type",
            accessor: "followUpTypes",
            visibility: {
                canColumnShow: false,
                showByDefault: false,
                hasShowSetting: false,
            },
            filterOptions: [{
                filterComponent: SearchSelectWithList(customFollowUpList,
                    (followUp: any) =>
                        <ColorTag
                            hexCode={followUp.hexColor}
                            followUpTypeName={followUp.name}/>,
                    "id",
                    "Select Follow-Up Types...",
                    true,
                    "followUpTypes"),
            }],
        },
        {
            label: "Reorder Number", accessor: "reordered", visibility: {canColumnShow: false},
            filterOptions: [{
                filterComponent: SearchSelectWithIndeterminateBoolean("Select Has Update...", "reordered"),
                overrideFilterAccessor: "reordered",
                overrideFilterLabel: "Has Update",
            }]
        },
        {
            label: "Manual Override", accessor: "manualOverride", visibility: {canColumnShow: false},
            filterOptions: [{
                filterComponent: SearchSelectWithIndeterminateBoolean("Select Manual Override...", "manualOverride"),
                overrideFilterLabel: "Manual Override",
                overrideFilterAccessor: "manualOverride"
            }],
        }

    ]

    return (
        <div className="px-5 py-5">
            <div className="flex justify-between">
                <Breadcrumb crumbs={crumbs}/>
                <div>
                </div>
            </div>

            <DataTable
                columns={columns}
                quickFilters={quickFilters}
                actions={{
                    fetchAction: useSearchOrdersQuery,
                    exportAction: triggerExportProductDataQuery
                }}

                userSettingGroup='Orders'
                buttonOptions={{
                    addButton: {
                        button: <IconButton
                            className={'h-[35px] min-w-[52px] rounded-[11px]'}
                            icon={<FaPlus size={14}/>}
                            hasBackground={true}
                            onClick={() => {
                                navigate("/orders/create")
                            }}
                            title={"Create Order"}
                        />,
                        permissions: ["ORDR-C", "ORDR-B", "ORDR-R"]
                    },
                }}
                defaultFilter={{
                    ...defaultFilter
                }}
                defaultSortOptions={{
                    accessor: 'creationDate',
                    direction: 'desc'
                }}
                basePermission={"ORDR"}
            />

            {showFollowUpModal && <AddFollowUpForProduct show={showFollowUpModal}
                                                         setShow={setShowFollowUpModal}
                                                         isHoa={false}
                                                         btnName={"Update"}
                                                         currentProduct={curProduct}
                                                         orderId={orderId}
            />}
        </div>


    )
}
export default Orders